import { useMemo, useRef } from 'react';

import { CFPInputFieldPassword } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { Box, InputFieldModels } from '@bcp-nextgen-dx-component-factory/design-system';
import { Captcha, CollapsibleBlock, InvalidDataAlert } from '@components';
import { AppHelper, UIHelper, UrlHelper } from '@helpers';
import { useAccountChallengeSubmitForm, useLoginForm, useTranslation } from '@hooks';
import { useBlockStepManager } from '@modules';
import { LoginBlock, useSearchParamsStore, useServicesStore } from '@store';

const resources = {
	title: 'screens.login.form.password.title',
	link: {
		text: 'screens.login.form.password.link.text',
		url: 'screens.login.form.password.link.url',
	},
	input: {
		label: 'screens.login.form.password.input.label',
	},
	closed: {
		title: 'screens.login.form.password.closed.title',
		link: {
			text: 'screens.login.form.password.closed.link.text',
		},
	},
	button: {
		continue: 'screens.login.button.continue',
	},
};

export interface PasswordBlockProps {
	isActive?: boolean;
}

export const PasswordBlock = ({ isActive }: PasswordBlockProps) => {
	const { t } = useTranslation();
	const { blocks, goto } = useBlockStepManager();
	const { alias, password, showCaptcha, captchaValue, setState } = useLoginForm();
	const { submitForm } = useAccountChallengeSubmitForm();

	const accountLogin = useServicesStore((state) => state.accountLogin) || {};
	const clientId = useSearchParamsStore((store) => store.clientId);
	const language = useSearchParamsStore((store) => store.language);

	const inputRef = useRef<InputFieldModels.InputFieldRef>(null);

	/* store Password value in loginFormStore on input change */
	const handleInputFieldChange = (value: string) => {
		setState({ form: { password: value } });
	};

	const sanitizeAlphanumeric = () => {
		const el = inputRef.current;
		const elValue = el?.getValue();
		const clean = elValue?.replace(/[^A-Za-z0-9]/g, '');

		// Only touch the DOM if needed (prevents cursor jumps)
		if (el && elValue !== clean) {
			el.setNativeValue(clean || '');
		}
	};

	const handleClosedContentLinkClick = () => {
		setState({ form: { password: '' } });
		goto(LoginBlock.Password);
	};

	const handleOpenContentButtonClick = async () => {
		if (accountLogin.needsTaxNumber) {
			goto(LoginBlock.TaxNumber);
		} else {
			/* doesn't need TaxNumber, submit AccountChallenge form post */
			submitForm();
		}
	};

	const handleCaptchaChange = (value: string) => {
		setState({ form: { captchaValue: value } });
	};

	const openContentLinkHref = useMemo(() => {
		const extractPathSegment = (url: string): string => {
			const match = url.match(/\/(a|b)\//);
			return match?.length && match.length > 1 ? `${match[1]}/` : '';
		};

		const geo = extractPathSegment(window.location.pathname);

		const params = [
			{ key: 'alias', value: alias },
			{ key: 'cred', value: 'pw' },
			{ key: 'language', value: language?.toLowerCase() },
		];

		const redirectUrl = t(resources.link.url, {
			geo,
			searchParams: UrlHelper.createQueryParams(params),
		});
		return redirectUrl ? redirectUrl : '';
	}, [alias, language, t]);

	const closedContentValue =
		password &&
		Array.from(password)
			.map(() => '*')
			.join('');

	const corporateClientsList = t('configs.corporateClientsList');
	const isCorporateClient = AppHelper.isCorporateClient(corporateClientsList, clientId);
	const isLastBlock = blocks && blocks.length > 0 && blocks[blocks.length - 1].name === LoginBlock.Password;

	return (
		<CollapsibleBlock
			isOpen={isActive}
			openContent={{
				label: {
					value: t(resources.title),
				},
				links: [
					{
						label: isCorporateClient ? t(resources.link.text) : undefined,
						href: openContentLinkHref,
					},
				],
				button: isLastBlock
					? {
							label: t(resources.button.continue),
							disabled: !password || password.length < 6,
							onClick: handleOpenContentButtonClick,
					  }
					: undefined,
				children: (
					<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(32) }}>
						<CFPInputFieldPassword
							ref={inputRef}
							name="password"
							autoFocus
							maxLength={8}
							label={t(resources.input.label)}
							// do not use defaultValue or value to not expose the value in html
							// defaultValue={password}
							// value={password}
							onChange={(v) => handleInputFieldChange(v?.toString() || '')}
							onInput={sanitizeAlphanumeric}
						/>

						{isLastBlock && <InvalidDataAlert />}
						{isLastBlock && showCaptcha && (
							<Box styles={{ marginTop: UIHelper.rem(32) }}>
								<Captcha value={captchaValue} onChange={handleCaptchaChange} />
							</Box>
						)}
					</Box>
				),
			}}
			closedContent={{
				label: t(resources.closed.title),
				value: closedContentValue,
				link: {
					label: t(resources.closed.link.text),
					onClick: handleClosedContentLinkClick,
				},
			}}
		/>
	);
};
